import apiInvitado from "../api/Invitado/api-invitado";

/**
 * Pregunta por la vigencia del token una sola vez por ráfaga.
 *
 * Dos sitios preguntan lo mismo: la comprobación de acceso, en cada cambio de
 * pantalla, y el interceptor, en cada petición. Navegar entre pestañas son
 * decenas de preguntas por el mismo token en pocos segundos, y basta con que
 * una tropiece para cerrar la sesión. Cuantas más veces se pregunta, más
 * ocasiones hay de que una falle.
 *
 * Aquí se comparte la respuesta: la primera pregunta la hace, las que lleguen
 * mientras tanto esperan esa misma, y durante un rato se reutiliza en vez de
 * volver a preguntar. Es el mismo remedio que ya se usa con el token de
 * invitado.
 *
 * Esto no sustituye al arreglo del servicio de usuarios, que responde igual
 * cuando el token no sirve y cuando no pudo comprobarlo. Reduce las ocasiones
 * de tropezar, no la causa.
 */

/** Cuánto vale una respuesta antes de volver a preguntar. */
export const VIGENCIA_DE_LA_RESPUESTA_MS = 60 * 1000;

/** Cuando al token le queda menos que esto, se confirma contra el backend antes de usarlo. */
export const MARGEN_DE_CONFIRMACION_MS = 5 * 60 * 1000;

/**
 * Lee la fecha de expiración que el propio JWT lleva dentro, en milisegundos.
 * Devuelve null si el token no se puede interpretar: en ese caso no se asume nada.
 */
export const expiracionDelToken = (token: string): number | null => {
  try {
    const carga = token.split(".")[1];
    if (!carga) return null;

    const json = atob(carga.replace(/-/g, "+").replace(/_/g, "/"));
    const { exp } = JSON.parse(json);

    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return null;
  }
};

export const tokenVencido = (token: string, ahora: number = Date.now()): boolean => {
  const expiracion = expiracionDelToken(token);
  return expiracion !== null && expiracion <= ahora;
};

export const porVencer = (token: string, ahora: number = Date.now()): boolean => {
  const expiracion = expiracionDelToken(token);
  if (expiracion === null) return true; // ilegible: que decida el backend
  return expiracion - ahora <= MARGEN_DE_CONFIRMACION_MS;
};

type Respuesta = any;

let recordado: { token: string; respuesta: Respuesta; cuando: number } | null = null;
let enCurso: { token: string; promesa: Promise<Respuesta> } | null = null;

/** Solo para las pruebas: olvida lo recordado. */
export const olvidarVigencia = () => {
  recordado = null;
  enCurso = null;
};

/**
 * La respuesta del servicio de usuarios sobre este token.
 *
 * Solo se recuerda lo que sirve para seguir adelante. Una respuesta que
 * cierra la sesión no se guarda: para cuando vuelva a preguntarse ya no habrá
 * sesión que cerrar, y recordar un "no" ajeno al token podría arrastrarlo a
 * una sesión nueva.
 */
export const confirmarVigencia = async (
  token: string,
  ahora: number = Date.now()
): Promise<Respuesta> => {
  if (
    recordado &&
    recordado.token === token &&
    ahora - recordado.cuando < VIGENCIA_DE_LA_RESPUESTA_MS
  ) {
    return recordado.respuesta;
  }

  if (enCurso && enCurso.token === token) return enCurso.promesa;

  const promesa = apiInvitado
    .validateToken(token)
    .then((respuesta: Respuesta) => {
      if (respuesta?.status === 200) {
        recordado = { token, respuesta, cuando: ahora };
      }
      return respuesta;
    })
    .finally(() => {
      enCurso = null;
    });

  enCurso = { token, promesa };
  return promesa;
};
